import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useSupportCase } from "@/hooks/useSupportCase";
import { useSupportUnread } from "@/hooks/useSupportUnread";
import { getSupportCategoryLabel } from "@/utils/supportCategoryLabel";
import {
  STATUS_LABELS,
  SERVICE_CATEGORIES,
} from "@/types/support";
import { useLanguage } from "@/context/LanguageContext";
import { FAQSection } from "@/components/support/FAQSection";
import { ContactCards } from "@/components/support/ContactCards";
import { useForm } from "react-hook-form";
import { ComplaintFormValues, Step } from "@/components/support/types";
import { ComplaintType } from "@/components/support/ComplaintTypeSelector";
import { toast } from "sonner";
import { Form } from "@/components/ui/form";
import { ComplaintTypeSelector } from "@/components/support/ComplaintTypeSelector";
import { RegistrationStep } from "@/components/support/steps/RegistrationStep";
import { DetailsStep } from "@/components/support/steps/DetailsStep";
import { IncorrectDetailsSection } from "@/components/support/steps/IncorrectDetailsSection";
import { Loader2, Check, ArrowRight, Info } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { ProcessSteps } from "@/components/support/ProcessSteps";
import { WizardStepIndicator } from "@/components/support/WizardStepIndicator";
import TermsDialog from "@/components/footer/terms_dialog";
import { SupportGuides } from "@/components/support/SupportGuides";
import { useTheme } from "@/context/ThemeContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import BtnNavigateBack from "@/components/ui/btn_navigate_back";
import { useShowNavigateBack } from "@/hooks/useShowNavigateBack";
import {
  getSupportStatusColor,
  getSupportTypeIcon,
} from "@/utils/supportUiUtils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MobilePillTabs } from "@/components/ui/mobile-pill-tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Pagination } from "@/components/ui/pagination";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertCircle,
  Wrench,
  Calendar,
  MessageSquare,
  FileText,
  Plus,
  Car,
  Trash2,
} from "lucide-react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { supportLabels } from "@/i18n";
import { getLanguage } from "@/utils/languageUtils";
import { SupportCaseFileUpload } from "@/components/support/SupportCaseFileUpload";
import { useSupportConversation } from "@/hooks/useSupportConversation";
import { ComplaintFollowUpSummary } from "@/components/support/ComplaintFollowUpSummary";
import {
  emptyFollowUpValues,
  isComplaintFollowUpComplete,
} from "@/components/support/complaintFollowUpCatalog";

const Support = () => {
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedCaseType, setSelectedCaseType] = useState<
    "complaint" | "service" | null
  >(null);
  const [complaintStep, setComplaintStep] = useState<Step>("registration");
  const [selectedComplaintType, setSelectedComplaintType] =
    useState<ComplaintType | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [supportCategory, setSupportCategory] = useState<string>("");
  const [supportStep, setSupportStep] = useState<"category" | "details">(
    "category"
  );
  const [selectedSupportFiles, setSelectedSupportFiles] = useState<File[]>([]);
  const [selectedComplaintFiles, setSelectedComplaintFiles] = useState<File[]>([]);
  const [caseDocumentFiles, setCaseDocumentFiles] = useState<File[]>([]);
  const [isSubmittingSupport, setIsSubmittingSupport] = useState(false);
  const [policyDialogOpen, setPolicyDialogOpen] = useState(false);
  const itemsPerPage = 10;
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const isMobile = useIsMobile();
  const { isNative } = useNativePlatform();
  const useCompactCaseList = isMobile || isNative;
  const showPageBack = useShowNavigateBack();
  const { user } = useAuth();
  const { language } = useLanguage();
  const categoryLabel = (category: string) =>
    getSupportCategoryLabel(category, language);
  const {
    createSupportCaseAsync,
    mySupportCases,
    mySupportCasesMeta,
    isLoadingMyCases,
    myCasesError,
    supportCase: loadedCase,
    isLoadingCase,
    sendMessageAsync,
    isSendingMessage,
    refetchSupportCase,
    uploadDocumentAsync,
    deleteDocumentAsync,
    isUploadingDocument,
    isDeletingDocument,
  } = useSupportCase(String(user?.id || ""), selectedCaseId, {
    status: statusFilter,
    page: currentPage,
    limit: itemsPerPage,
  });
  const { data: supportUnread } = useSupportUnread();
  const myCasesUnreadCount = supportUnread?.count ?? 0;
  const selectedCase =
    loadedCase ??
    (selectedCaseId
      ? mySupportCases.find((c) => c.id === selectedCaseId) ?? null
      : null);
  const conversationItems = useSupportConversation(
    selectedCase?.comments,
    selectedCase?.messages,
    selectedCase ? { customerId: selectedCase.customerId } : undefined,
  );
  const isCaseEditable =
    selectedCase?.status !== "closed" && selectedCase?.status !== "resolved";

  const handleUploadCaseDocuments = async () => {
    if (!selectedCaseId || caseDocumentFiles.length === 0 || !isCaseEditable) return;
    try {
      for (const file of caseDocumentFiles) {
        await uploadDocumentAsync({ caseId: selectedCaseId, file });
      }
      setCaseDocumentFiles([]);
      toast.success(supportLabels_i18n.documentsUploaded ?? "Dokument uppladdade");
      refetchSupportCase();
    } catch {
      toast.error(supportLabels_i18n.error);
    }
  };

  const handleDeleteCaseDocument = async (documentId: string) => {
    if (!selectedCaseId || !isCaseEditable) return;
    try {
      await deleteDocumentAsync({ caseId: selectedCaseId, documentId });
      toast.success(supportLabels_i18n.documentDeleted ?? "Dokument borttaget");
      refetchSupportCase();
    } catch {
      toast.error(supportLabels_i18n.error);
    }
  };
  const supportLabels_i18n = supportLabels(getLanguage());

  const complaintWizardSteps = [
    {
      id: "registration",
      label: supportLabels_i18n.wizardSteps?.registration ?? "Registrering",
    },
    {
      id: "type",
      label: supportLabels_i18n.wizardSteps?.type ?? "Typ",
    },
    {
      id: "details",
      label: supportLabels_i18n.wizardSteps?.details ?? "Detaljer",
    },
  ];

  const serviceWizardSteps = [
    {
      id: "category",
      label: supportLabels_i18n.wizardSteps?.category ?? "Kategori",
    },
    {
      id: "details",
      label: supportLabels_i18n.wizardSteps?.details ?? "Detaljer",
    },
  ];

  // Complaint form
  const complaintForm = useForm<ComplaintFormValues>({
    defaultValues: {
      registrationNumber: "",
      description: "",
      damageLocation: undefined,
      damageType: undefined,
      engineComponent: undefined,
      interiorSection: undefined,
      wheelType: undefined,
      files: null,
    },
  });
  const watchedFollowUps = complaintForm.watch([
    "description",
    "damageLocation",
    "damageType",
    "engineComponent",
    "interiorSection",
    "wheelType",
  ]);
  const canSubmitComplaint =
    !isSubmitting &&
    Boolean(watchedFollowUps[0]?.trim()) &&
    isComplaintFollowUpComplete(selectedComplaintType?.id, {
      damageLocation: watchedFollowUps[1],
      damageType: watchedFollowUps[2],
      engineComponent: watchedFollowUps[3],
      interiorSection: watchedFollowUps[4],
      wheelType: watchedFollowUps[5],
    });

  // Support case form
  const supportCaseForm = useForm<{
    category: string;
    description: string;
    files: FileList | null;
  }>({
    defaultValues: {
      category: "",
      description: "",
      files: null,
    },
  });

  const handleSendMessage = async () => {
    if (!selectedCaseId || !newMessage.trim()) return;
    try {
      await sendMessageAsync({ caseId: selectedCaseId, content: newMessage.trim() });
      setNewMessage("");
      toast.success(supportLabels_i18n.messageSent);
      refetchSupportCase();
    } catch {
      toast.error(supportLabels_i18n.error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "not_started":
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200";
      case "in_progress":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
      case "resolved":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case "closed":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200";
    }
  };

  const getTypeIcon = (type: string) => {
    return type === "complaint" ? (
      <AlertCircle className="h-4 w-4 text-red-500" />
    ) : (
      <Wrench className="h-4 w-4 text-blue-500" />
    );
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    setCurrentPage(1); // Reset to first page when changing filter
  };

  const handleComplaintSubmit = async (data: ComplaintFormValues) => {
    if (!selectedComplaintType || !user?.id) return;

    setIsSubmitting(true);
    try {
      await createSupportCaseAsync({
        regNr: data.registrationNumber,
        type: "complaint",
        customerId: user.id,
        category: selectedComplaintType.id,
        description: data.description,
        files: selectedComplaintFiles.length > 0 ? selectedComplaintFiles : undefined,
        damageLocation: data.damageLocation,
        damageType: data.damageType,
        engineComponent: data.engineComponent,
        interiorSection: data.interiorSection,
        wheelType: data.wheelType,
      });

      toast.success(supportLabels_i18n.complaintSent, { description: supportLabels_i18n.complaintSentDescription });

      complaintForm.reset();
      setSelectedComplaintFiles([]);
      setSelectedComplaintType(null);
      setComplaintStep("registration");
      setShowCreateDialog(false);
      setSelectedCaseType(null);
    } catch (error) {
      toast.error(supportLabels_i18n.error, { description: supportLabels_i18n.complaintError });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleComplaintNext = async () => {
    if (complaintStep === "registration") {
      const errors = complaintForm.formState.errors;
      if (errors.registrationNumber) {
        return;
      }

      setIsSearching(true);
      try {
        complaintForm.clearErrors("registrationNumber");
        const formWithSearch = complaintForm as typeof complaintForm & {
          handleSearch?: () => Promise<boolean>;
        };
        if (formWithSearch.handleSearch) {
          const found = await formWithSearch.handleSearch();
          if (found) {
            setComplaintStep("type");
          }
        } else {
          setComplaintStep("type");
        }
      } finally {
        setIsSearching(false);
      }
    }
  };

  const handleComplaintTypeSelect = (type: ComplaintType) => {
    setSelectedComplaintType(type);
    const cleared = emptyFollowUpValues();
    complaintForm.setValue("damageLocation", cleared.damageLocation);
    complaintForm.setValue("damageType", cleared.damageType);
    complaintForm.setValue("engineComponent", cleared.engineComponent);
    complaintForm.setValue("interiorSection", cleared.interiorSection);
    complaintForm.setValue("wheelType", cleared.wheelType);
    setComplaintStep("details");
  };

  const handleSupportCaseSubmit = async (data: {
    category: string;
    description: string;
    files: FileList | null;
  }) => {
    setIsSubmittingSupport(true);
    try {
      const files =
        selectedSupportFiles.length > 0 ? selectedSupportFiles : undefined;

      await createSupportCaseAsync({
        type: "service",
        customerId: user?.id?.toString() || "",
        category: data.category,
        description: data.description,
        files: files,
      });

      toast.success(supportLabels_i18n.serviceCaseCreated, { description: supportLabels_i18n.serviceCaseCreatedDescription });

      supportCaseForm.reset();
      setSupportCategory("");
      setSupportStep("category");
      setSelectedSupportFiles([]);
      setShowCreateDialog(false);
      setSelectedCaseType(null);
    } catch (error) {
      toast.error(supportLabels_i18n.error, { description: supportLabels_i18n.serviceCaseError });
    } finally {
      setIsSubmittingSupport(false);
    }
  };

  const handleSupportCategoryNext = () => {
    if (supportCategory) {
      setSupportStep("details");
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "technical_issue":
        return <Wrench className="h-5 w-5" />;
      case "buying_selling":
        return <Car className="h-5 w-5" />;
      case "other":
        return <AlertCircle className="h-5 w-5" />;
      default:
        return <AlertCircle className="h-5 w-5" />;
    }
  };

  const handleSupportCategorySelect = (category: string) => {
    setSupportCategory(category);
    supportCaseForm.setValue("category", category);
  };

  const handleCloseCreateDialog = () => {
    setShowCreateDialog(false);
    setSelectedCaseType(null);
    complaintForm.reset();
    setSelectedComplaintType(null);
    setComplaintStep("registration");
    setSelectedComplaintFiles([]);
    supportCaseForm.reset();
    setSupportCategory("");
    setSupportStep("category");
    setSelectedSupportFiles([]);
  };

  return (
    <div className="relative">
      <div className="container mx-auto px-3 sm:px-6 py-3 sm:py-6 max-w-4xl animate-fade-in pb-safe">
        {showPageBack && <BtnNavigateBack />}

        <h1
          className={`text-2xl md:text-2xl mb-4 font-bold text-center ${
            isDark ? "text-white" : "text-gray-800"
          }`}
        >
          {supportLabels_i18n.title}
        </h1>

        {/* Overview + Mina ärenden for all authenticated users */}
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="space-y-6"
        >
          {isMobile ? (
            <MobilePillTabs
              equalWidth
              items={[
                { value: "overview", label: supportLabels_i18n.overview },
                {
                  value: "my-cases",
                  label: (
                    <span className="relative inline-flex items-center gap-1.5">
                      {supportLabels_i18n.myCases}
                      {myCasesUnreadCount > 0 && (
                        <span className="h-2 w-2 rounded-full bg-red-500" />
                      )}
                    </span>
                  ),
                },
              ]}
              value={activeTab as "overview" | "my-cases"}
              onChange={setActiveTab}
            />
          ) : (
            <TabsList className="grid w-full grid-cols-2 h-auto p-1">
              <TabsTrigger value="overview" className="text-sm py-2 px-3">
                {supportLabels_i18n.overview}
              </TabsTrigger>
              <TabsTrigger
                value="my-cases"
                className="text-sm py-2 px-3 relative"
              >
                {supportLabels_i18n.myCases}
                {myCasesUnreadCount > 0 && (
                  <span className="absolute top-1.5 right-2 h-2 w-2 rounded-full bg-red-500" />
                )}
              </TabsTrigger>
            </TabsList>
          )}

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <FAQSection faqItems={supportLabels_i18n.faq?.items || []} />
            <ContactCards />
            <SupportGuides />
          </TabsContent>

          {/* My Cases Tab */}
          <TabsContent value="my-cases" className="space-y-6">
            <Card className="glass-card border-border/60">
              <CardHeader>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
                    {/* Status Filter */}
                    <Select
                      value={statusFilter}
                      onValueChange={handleStatusFilterChange}
                    >
                      <SelectTrigger className="w-full sm:w-48">
                        <SelectValue
                          placeholder={supportLabels_i18n.filterByStatus}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">
                          {supportLabels_i18n.allStatuses}
                        </SelectItem>
                        <SelectItem value="not_started">
                          {supportLabels_i18n.notStarted}
                        </SelectItem>
                        <SelectItem value="in_progress">
                          {supportLabels_i18n.inProgress}
                        </SelectItem>
                        <SelectItem value="resolved">
                          {supportLabels_i18n.resolved}
                        </SelectItem>
                        <SelectItem value="closed">
                          {supportLabels_i18n.closed}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={() => {
                        setShowCreateDialog(true);
                        setSelectedCaseType(null);
                      }}
                      className="flex items-center gap-2 whitespace-nowrap"
                    >
                      <Plus className="h-4 w-4" />
                      {supportLabels_i18n.createCase}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {isLoadingMyCases ? (
                  <div className="text-center py-8">
                    <div className="text-lg">
                      {supportLabels_i18n.loadingCases}
                    </div>
                  </div>
                ) : myCasesError ? (
                  <div className="text-center py-8">
                    <div className="text-lg text-red-500">
                      {supportLabels_i18n.loadingErrorGeneric ??
                        supportLabels_i18n.error}
                    </div>
                  </div>
                ) : mySupportCases.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                    <div className="text-lg text-gray-500">
                      {statusFilter === "all"
                        ? supportLabels_i18n.noCases
                        : supportLabels_i18n.noCasesWithStatus}
                    </div>
                    <div className="text-sm text-gray-400 mt-2">
                      {statusFilter === "all"
                        ? supportLabels_i18n.noCasesDescription
                        : supportLabels_i18n.noCasesWithStatusDescription}
                    </div>
                  </div>
                ) : useCompactCaseList ? (
                  <div className="space-y-3">
                    {mySupportCases.map((case_) => (
                      <Card
                        key={case_.id}
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => setSelectedCaseId(case_.id)}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-center gap-3">
                            {getTypeIcon(case_.type)}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-medium text-sm">
                                  {supportLabels_i18n.caseNumber.replace(
                                    "{id}",
                                    case_.caseRef || case_.id,
                                  )}
                                </h3>
                                {case_.hasUnread && (
                                  <span className="h-2 w-2 rounded-full bg-red-500 shrink-0" />
                                )}
                                <Badge
                                  className={`${getStatusColor(
                                    case_.status,
                                  )} text-xs`}
                                >
                                  {
                                    STATUS_LABELS[
                                      case_.status as keyof typeof STATUS_LABELS
                                    ]
                                  }
                                </Badge>
                              </div>
                              <p className="text-xs text-muted-foreground mb-1">
                                {categoryLabel(case_.category)}
                              </p>
                              <p className="text-xs text-muted-foreground line-clamp-1">
                                {case_.description}
                              </p>
                              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                                <div className="flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {format(
                                    new Date(case_.created),
                                    "dd MMM yyyy",
                                    { locale: sv },
                                  )}
                                </div>
                                {case_.vehicleRegNr && (
                                  <div className="flex items-center gap-1">
                                    <FileText className="h-3 w-3" />
                                    {case_.vehicleRegNr}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-md border overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50">
                          <tr className="border-b">
                            <th className="text-left p-2 font-medium">ID</th>
                            <th className="text-left p-2 font-medium">
                              {supportLabels_i18n.type}
                            </th>
                            <th className="text-left p-2 font-medium">
                              {supportLabels_i18n.category}
                            </th>
                            <th className="text-left p-2 font-medium">
                              {supportLabels_i18n.status}
                            </th>
                            <th className="text-left p-2 font-medium hidden lg:table-cell">
                              {supportLabels_i18n.registrationNumber}
                            </th>
                            <th className="text-left p-2 font-medium">
                              {supportLabels_i18n.created}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {mySupportCases.map((case_) => (
                            <tr
                              key={case_.id}
                              className="border-b cursor-pointer hover:bg-muted/40"
                              onClick={() => setSelectedCaseId(case_.id)}
                            >
                              <td className="p-2">
                                <div className="flex items-center gap-1.5 min-w-0">
                                  {getSupportTypeIcon(case_.type)}
                                  <span className="truncate max-w-[140px]">
                                    {case_.caseRef || case_.id}
                                  </span>
                                  {case_.hasUnread ? (
                                    <span className="h-2 w-2 rounded-full bg-red-500 shrink-0" />
                                  ) : null}
                                </div>
                              </td>
                              <td className="p-2">
                                <Badge variant="outline" className="text-xs">
                                  {case_.type === "complaint"
                                    ? supportLabels_i18n.complaint
                                    : supportLabels_i18n.service}
                                </Badge>
                              </td>
                              <td className="p-2 whitespace-nowrap">
                                {categoryLabel(case_.category)}
                              </td>
                              <td className="p-2">
                                <Badge
                                  className={`text-xs ${getSupportStatusColor(case_.status)}`}
                                >
                                  {
                                    STATUS_LABELS[
                                      case_.status as keyof typeof STATUS_LABELS
                                    ]
                                  }
                                </Badge>
                              </td>
                              <td className="p-2 hidden lg:table-cell whitespace-nowrap text-muted-foreground">
                                {case_.vehicleRegNr || "—"}
                              </td>
                              <td className="p-2 whitespace-nowrap">
                                <span className="inline-flex items-center gap-1 text-xs">
                                  <Calendar className="h-3 w-3" />
                                  {format(
                                    new Date(case_.created),
                                    "dd/MM/yyyy",
                                  )}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {!isLoadingMyCases &&
                  !myCasesError &&
                  mySupportCases.length > 0 &&
                  mySupportCasesMeta.totalPages > 1 && (
                    <div className="flex justify-center mt-6">
                      <Pagination
                        currentPage={currentPage}
                        totalPages={mySupportCasesMeta.totalPages}
                        totalItems={mySupportCasesMeta.total}
                        itemsPerPage={itemsPerPage}
                        onPageChange={handlePageChange}
                        onItemsPerPageChange={() => {}}
                        itemsPerPageOptions={[10]}
                      />
                    </div>
                  )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Create Case Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={handleCloseCreateDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            {!selectedCaseType ? (
              <div className="space-y-4 py-4">
                <DialogHeader>
                  <DialogTitle>{supportLabels_i18n.createCase}</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-muted-foreground mb-4">
                  {supportLabels_i18n.selectCaseType}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => setSelectedCaseType("complaint")}
                  >
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <AlertCircle className="h-5 w-5 text-red-500" />
                        {supportLabels_i18n.createComplaint}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {supportLabels_i18n.complaintDescription}
                      </p>
                    </CardContent>
                  </Card>

                  <Card
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => {
                      setSelectedCaseType("service");
                      setSupportStep("category");
                    }}
                  >
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Wrench className="h-5 w-5 text-blue-500" />
                        {supportLabels_i18n.createServiceCase}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {supportLabels_i18n.serviceDescription}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ) : selectedCaseType === "complaint" ? (
              <div className="py-4">
                <DialogHeader className="space-y-1 sm:space-y-2">
                  <DialogTitle className="text-base sm:text-lg md:text-xl">
                    {supportLabels_i18n.newComplaint}
                  </DialogTitle>
                  <DialogDescription className="text-xs sm:text-sm">
                    {complaintStep === "registration" &&
                      supportLabels_i18n.registrationStep}
                    {complaintStep === "type" && supportLabels_i18n.typeStep}
                    {complaintStep === "details" &&
                      supportLabels_i18n.detailsStep}
                  </DialogDescription>
                </DialogHeader>
                <WizardStepIndicator
                  steps={complaintWizardSteps}
                  currentStepId={complaintStep}
                  className="mt-4 px-1"
                />
                <Form {...complaintForm}>
                  <form
                    onSubmit={complaintForm.handleSubmit(handleComplaintSubmit)}
                    className="space-y-3 sm:space-y-4 mt-4"
                  >
                    {complaintStep === "registration" && (
                      <>
                        <div className="space-y-4 mb-4">
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {supportLabels_i18n.complaintIntro}
                          </p>
                          <ProcessSteps />
                          <Button
                            type="button"
                            variant="outline"
                            className="w-full hover:bg-primary/10 transition-all duration-300 group py-1 sm:py-2 text-xs sm:text-sm"
                            onClick={() => setPolicyDialogOpen(true)}
                          >
                            <Info className="mr-1 h-4 w-4" />
                            {supportLabels_i18n.readPolicy}
                            <ArrowRight className="ml-1 sm:ml-2 h-3 w-3 group-hover:translate-x-1 transition-transform duration-300" />
                          </Button>
                        </div>
                        <RegistrationStep form={complaintForm} />
                      </>
                    )}
                    {complaintStep === "type" && (
                      <ComplaintTypeSelector
                        onSelect={handleComplaintTypeSelect}
                      />
                    )}
                    {complaintStep === "details" &&
                      (selectedComplaintType?.id === "incorrect_details" ? (
                        <IncorrectDetailsSection
                          form={complaintForm}
                          files={selectedComplaintFiles}
                          onFilesChange={setSelectedComplaintFiles}
                        />
                      ) : (
                        <DetailsStep
                          form={complaintForm}
                          selectedType={selectedComplaintType}
                          files={selectedComplaintFiles}
                          onFilesChange={setSelectedComplaintFiles}
                        />
                      ))}
                    <div className="flex justify-between space-x-2 pt-2">
                      <div>
                        {complaintStep === "registration" ? (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setSelectedCaseType(null)}
                          >
                            {supportLabels_i18n.back}
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() =>
                              setComplaintStep(
                                complaintStep === "details"
                                  ? "type"
                                  : "registration"
                              )
                            }
                          >
                            {supportLabels_i18n.back}
                          </Button>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {complaintStep === "registration" && (
                          <Button
                            type="button"
                            onClick={handleComplaintNext}
                            disabled={isSearching}
                          >
                            {isSearching ? (
                              <div className="flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span>{supportLabels_i18n.searching}</span>
                              </div>
                            ) : (
                              supportLabels_i18n.next
                            )}
                          </Button>
                        )}
                        {complaintStep === "details" && (
                          <Button type="submit" disabled={!canSubmitComplaint}>
                            {isSubmitting ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                {supportLabels_i18n.creating}
                              </>
                            ) : (
                              supportLabels_i18n.sendComplaint
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </form>
                </Form>
              </div>
            ) : (
              <div className="py-4">
                <WizardStepIndicator
                  steps={serviceWizardSteps}
                  currentStepId={supportStep}
                  className="mb-4 px-1"
                />
                {supportStep === "category" ? (
                  <>
                    <DialogHeader>
                      <DialogTitle>{supportLabels_i18n.selectCategory}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-6 mt-4">
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 gap-3">
                          {SERVICE_CATEGORIES.map((category) => (
                            <div
                              key={category}
                              className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                                supportCategory === category
                                  ? "border-primary bg-primary/5"
                                  : "border-gray-200 hover:border-gray-300"
                              }`}
                              onClick={() =>
                                handleSupportCategorySelect(category)
                              }
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  {getCategoryIcon(category)}
                                  <span className="font-medium">
                                    {categoryLabel(category)}
                                  </span>
                                </div>
                                {supportCategory === category && (
                                  <Check className="h-5 w-5 text-primary" />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="flex justify-between gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setSelectedCaseType(null)}
                        >
                          {supportLabels_i18n.back}
                        </Button>
                        <Button
                          onClick={handleSupportCategoryNext}
                          disabled={!supportCategory}
                        >
                          {supportLabels_i18n.next}
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <DialogHeader>
                      <DialogTitle>
                        {supportLabels_i18n.describeProblem}
                      </DialogTitle>
                    </DialogHeader>
                    <form
                      onSubmit={supportCaseForm.handleSubmit(
                        handleSupportCaseSubmit
                      )}
                      className="space-y-6 mt-4"
                    >
                      <div className="space-y-4">
                        <div>
                          <Textarea
                            placeholder={
                              supportLabels_i18n.describeProblemPlaceholder
                            }
                            className="min-h-[120px]"
                            {...supportCaseForm.register("description", {
                              required: supportLabels_i18n.descriptionRequired,
                              minLength: {
                                value: 10,
                                message:
                                  supportLabels_i18n.descriptionMinLength,
                              },
                            })}
                          />
                          {supportCaseForm.formState.errors.description && (
                            <p className="text-sm text-red-500 mt-1">
                              {
                                supportCaseForm.formState.errors.description
                                  .message
                              }
                            </p>
                          )}
                        </div>
                        <SupportCaseFileUpload
                          files={selectedSupportFiles}
                          onFilesChange={setSelectedSupportFiles}
                        />
                      </div>
                      <div className="flex justify-between">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setSupportStep("category")}
                        >
                          {supportLabels_i18n.back}
                        </Button>
                        <Button
                          type="submit"
                          disabled={
                            !supportCategory ||
                            !supportCaseForm.watch("description")?.trim() ||
                            isSubmittingSupport
                          }
                        >
                          {isSubmittingSupport ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              {supportLabels_i18n.creating}
                            </>
                          ) : (
                            supportLabels_i18n.createServiceCase
                          )}
                        </Button>
                      </div>
                    </form>
                  </>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Reklamationspolicy via villkor/policies-dialog */}
        <TermsDialog
          open={policyDialogOpen}
          onOpenChange={setPolicyDialogOpen}
          initialTab="complaint"
          visibleTabs={["complaint"]}
        />

        {/* Case Details Modal */}
        {selectedCase && (
          <Dialog
            open={!!selectedCaseId}
            onOpenChange={(open) => {
              if (!open) {
                setSelectedCaseId(null);
                setNewMessage("");
                setCaseDocumentFiles([]);
              }
            }}
          >
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader className="pb-4 mb-2">
                <DialogTitle className="flex items-center gap-2">
                  {getTypeIcon(selectedCase.type)}
                  {supportLabels_i18n.caseNumber.replace(
                    "{id}",
                    selectedCase.caseRef || selectedCase.id,
                  )}
                </DialogTitle>
              </DialogHeader>

              <Tabs defaultValue="info" className="space-y-4 mt-2">
                <TabsList>
                  <TabsTrigger value="info">
                    {supportLabels_i18n.information}
                  </TabsTrigger>
                  <TabsTrigger value="messages">
                    {supportLabels_i18n.messages}
                  </TabsTrigger>
                  <TabsTrigger value="history">
                    {supportLabels_i18n.history}
                  </TabsTrigger>
                  <TabsTrigger value="documents">
                    {supportLabels_i18n.documents}
                  </TabsTrigger>
                </TabsList>

                {isLoadingCase && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {supportLabels_i18n.loading}
                  </div>
                )}

                {/* Information Tab */}
                <TabsContent value="info" className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        {supportLabels_i18n.status ?? "Status"}
                      </label>
                      <div className="mt-1">
                        <Badge className={getStatusColor(selectedCase.status)}>
                          {
                            STATUS_LABELS[
                              selectedCase.status as keyof typeof STATUS_LABELS
                            ]
                          }
                        </Badge>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        {supportLabels_i18n.type ?? "Typ"}
                      </label>
                      <div className="mt-1">
                        <Badge variant="outline">
                          {selectedCase.type === "complaint"
                            ? supportLabels_i18n.complaint
                            : supportLabels_i18n.service}
                        </Badge>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        {supportLabels_i18n.category ?? "Kategori"}
                      </label>
                      <p className="text-sm mt-1">
                        {categoryLabel(selectedCase.category)}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        {supportLabels_i18n.created}
                      </label>
                      <p className="text-sm mt-1">
                        {format(
                          new Date(selectedCase.created),
                          "dd MMM yyyy 'kl' HH:mm",
                          { locale: sv }
                        )}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        {supportLabels_i18n.caseReferenceNumber}
                      </label>
                      <p className="text-sm mt-1">
                        {selectedCase.caseRef || selectedCase.id}
                      </p>
                    </div>
                    {selectedCase.vehicleRegNr && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          {supportLabels_i18n.registrationNumber}
                        </label>
                        <p className="text-sm mt-1">
                          {selectedCase.vehicleRegNr}
                        </p>
                      </div>
                    )}
                  </div>

                  <ComplaintFollowUpSummary
                    category={selectedCase.category}
                    damageLocation={selectedCase.damageLocation}
                    damageType={selectedCase.damageType}
                    engineComponent={selectedCase.engineComponent}
                    interiorSection={selectedCase.interiorSection}
                    wheelType={selectedCase.wheelType}
                    language={language}
                  />

                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      {supportLabels_i18n.description}
                    </label>
                    <p className="text-sm mt-1 whitespace-pre-wrap">
                      {selectedCase.description}
                    </p>
                  </div>
                </TabsContent>

                {/* Messages Tab — comments + messages in one thread */}
                <TabsContent value="messages" className="space-y-4">
                  {conversationItems.length > 0 ? (
                    <div className="space-y-4">
                      {conversationItems.map((item) => {
                        const isFromCustomer = item.isFromCustomer;
                        return (
                          <div
                            key={item.id}
                            className={`flex ${isFromCustomer ? "justify-end" : "justify-start"}`}
                          >
                            <div
                              className={`max-w-[80%] p-3 rounded-lg ${
                                isFromCustomer
                                  ? "bg-primary/10 border border-primary/20"
                                  : "bg-muted border border-border"
                              }`}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-medium">
                                  {item.authorName}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {format(
                                    new Date(item.createdAt),
                                    "dd/MM/yyyy 'kl' HH:mm",
                                    { locale: sv },
                                  )}
                                </span>
                              </div>
                              <p className="text-sm whitespace-pre-wrap">
                                {item.content}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      {supportLabels_i18n.noMessages}
                    </div>
                  )}

                  <div className="border-t pt-4 space-y-3">
                    <Textarea
                      placeholder={supportLabels_i18n.writeMessagePlaceholder}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      className="min-h-[100px]"
                    />
                    <div className="flex justify-end">
                      <Button
                        onClick={handleSendMessage}
                        disabled={!newMessage.trim() || isSendingMessage}
                      >
                        {isSendingMessage ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            {supportLabels_i18n.sendingMessage}
                          </>
                        ) : (
                          supportLabels_i18n.sendMessage
                        )}
                      </Button>
                    </div>
                  </div>
                </TabsContent>

                {/* History Tab */}
                <TabsContent value="history" className="space-y-4">
                  {selectedCase.historyEvents &&
                  selectedCase.historyEvents.length > 0 ? (
                    <div className="space-y-4">
                      {selectedCase.historyEvents.map((event) => {
                        const isFromCustomer =
                          event.userName === selectedCase.customerName;
                        const isFromSystem =
                          event.userName === supportLabels_i18n.system ||
                          event.userName === supportLabels_i18n.adminUser;

                        return (
                          <div
                            key={event.id}
                            className={`flex ${
                              isFromCustomer ? "justify-start" : "justify-end"
                            }`}
                          >
                            <div
                              className={`max-w-[70%] ${
                                isFromCustomer ? "mr-auto" : "ml-auto"
                              }`}
                            >
                              <div
                                className={`p-3 rounded-lg ${
                                  isFromCustomer
                                    ? "bg-blue-50 border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800"
                                    : isFromSystem
                                    ? "bg-gray-50 border border-gray-200 dark:bg-gray-800 dark:border-gray-700"
                                    : "bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-800"
                                }`}
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  <span
                                    className={`text-sm font-medium ${
                                      isFromCustomer
                                        ? "text-blue-800 dark:text-blue-200"
                                        : isFromSystem
                                        ? "text-gray-800 dark:text-gray-200"
                                        : "text-green-800 dark:text-green-200"
                                    }`}
                                  >
                                    {event.action}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {format(
                                      new Date(event.timestamp),
                                      "dd/MM/yyyy 'kl' HH:mm",
                                      { locale: sv }
                                    )}
                                  </span>
                                </div>
                                <p
                                  className={`text-xs ${
                                    isFromCustomer
                                      ? "text-blue-600 dark:text-blue-300"
                                      : isFromSystem
                                      ? "text-gray-600 dark:text-gray-300"
                                      : "text-green-600 dark:text-green-300"
                                  }`}
                                >
                                  {supportLabels_i18n.by} {event.userName}
                                </p>
                                {event.details && (
                                  <div className="mt-2 text-xs text-gray-500">
                                    {event.details.oldValue &&
                                      event.details.newValue && (
                                        <p>
                                          {supportLabels_i18n.from}:{" "}
                                          {event.details.oldValue} →{" "}
                                          {supportLabels_i18n.to}:{" "}
                                          {event.details.newValue}
                                        </p>
                                      )}
                                    {event.details.fileName ? (
                                      <p>{event.details.fileName}</p>
                                    ) : null}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      {supportLabels_i18n.noHistory}
                    </div>
                  )}
                </TabsContent>

                {/* Documents Tab */}
                <TabsContent value="documents" className="space-y-4">
                  {selectedCase.files && selectedCase.files.length > 0 ? (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium">
                        {supportLabels_i18n.uploadedDocuments}
                      </h4>
                      {selectedCase.files.map((file) => (
                        <div
                          key={file.id}
                          className="flex items-center gap-3 p-3 border rounded-lg"
                        >
                          <FileText className="h-5 w-5 text-gray-400" />
                          <div className="flex-1">
                            <p className="text-sm font-medium">{file.name}</p>
                            <p className="text-xs text-gray-500">
                              {(file.size / 1024).toFixed(1)} KB{" "}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const link = document.createElement("a");
                                link.href = file.url;
                                link.download = file.name;
                                link.target = "_blank";
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }}
                            >
                              {supportLabels_i18n.download}
                            </Button>
                            {isCaseEditable && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                disabled={isDeletingDocument}
                                onClick={() => handleDeleteCaseDocument(file.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      {supportLabels_i18n.noDocuments}
                    </div>
                  )}

                  {isCaseEditable ? (
                    <div className="border-t pt-4 space-y-3">
                      <SupportCaseFileUpload
                        files={caseDocumentFiles}
                        onFilesChange={setCaseDocumentFiles}
                      />
                      <div className="flex justify-end">
                        <Button
                          onClick={handleUploadCaseDocuments}
                          disabled={
                            caseDocumentFiles.length === 0 || isUploadingDocument
                          }
                        >
                          {isUploadingDocument ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              {supportLabels_i18n.uploading ?? "Laddar upp..."}
                            </>
                          ) : (
                            supportLabels_i18n.uploadDocuments ?? "Ladda upp dokument"
                          )}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center pt-2">
                      {supportLabels_i18n.caseClosedDocumentsLocked ??
                        "Avslutade ärenden kan inte ändra dokument."}
                    </p>
                  )}
                </TabsContent>
              </Tabs>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
};

export default Support;
